import { Injectable, Injector } from '@angular/core';
import { LogService } from '@core/services/system/logger';
import { ILogger } from '@paymash/logging';
import { Buffer } from 'buffer';
import NexoCrypto from '../security/nexo-crypto.class';
import { SERVER_CONFIG } from '@shared/constants/server.const';
import { AdyenUtils } from '../adyen-utils';
import { Actions, ofType } from '@ngrx/effects';
import { AddSoftPosTerminalAdyenSuccess, ErrorActionTerminal } from '@state/settings';
import { catchError, filter, map, timeout } from 'rxjs/operators';
import { IActiveTerminal } from '@shared/interfaces';
import { softPOsTerminalHelper } from '@core/services/system/adyen/services/helpers/soft-pos-terminal.helper';
import { Observable, Subject, take, throwError, firstValueFrom } from 'rxjs';
import { AdyenPaymentApi, AdyenTerminalApi } from '@core/services/system/adyen/services/api';
import { MakePaymentResponse, PaymentResponse } from '@core/services/system/adyen/adyen-types';
import {
  AdyenPaymentError,
  AdyenTerminalInfo,
  AdyenTerminalSettingsInfo,
  SaleToPoiSecuredMessage,
  SecurityKey,
} from '@core/services/system/adyen/classes';
import { AppLauncher, CanOpenURLResult, OpenURLResult } from '@core/capacitor-plugins/capacitor-plugins.class';
import { StorageKeys } from '@shared/constants';
import { LocalStorage } from '@core/services/utils/localstorage.utils';
import { PaymentMethod } from '@shared/classes/payment-method.class';
import { PaymentService } from '@core/services/system/payment.service';
import { PosSettingsService } from '@core/services/system/pos-settings.service';
import { AdyenSoftPosTargetAction, ErrorCondition } from '@core/services/system/adyen/enums';
import { AdyenPaymentErrors } from '@core/services/system/adyen/costants';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class AdyenSoftPosService {
  private readonly settings: Record<string, string> = SERVER_CONFIG.ADYEN_PAYMENT_APPLICATION_SETTINGS;
  private readonly logger: ILogger = this.logService.createLogger('AdyenSoftPosService');
  private paymentResultSubject$: Subject<MakePaymentResponse>;
  private timeForWaitApplicationResponse: number = 90000;
  private securityKeyCache: SecurityKey | null = null;

  constructor(
    private inject: Injector,
    private logService: LogService,
    private adyenUtils: AdyenUtils,
    private actions$: Actions,
    private adyenApiService: AdyenPaymentApi,
    private terminalApi: AdyenTerminalApi,
    private localStorage: LocalStorage,
    private posSettingsService: PosSettingsService,
    private translateService: TranslateService
  ) {}

  get softPOSAddedResultAction(): Observable<{ terminal: IActiveTerminal } | { error: unknown }> {
    return this.actions$.pipe(
      ofType(AddSoftPosTerminalAdyenSuccess, ErrorActionTerminal),
      map((action): { terminal: IActiveTerminal } | { error: unknown } => {
        if (action?.['terminal']) {
          return {
            terminal: softPOsTerminalHelper(action?.['terminal']),
          };
        }
        if (action?.['error']) {
          this.logger.error(action?.['error'], 'softPOSAddedResultAction');
          return { error: action['error'] };
        }
        this.logger.error(action, 'softPOSAddedResultAction unknown error');
        return { error: null };
      })
    );
  }

  isSoftPosEvent(url: URL): boolean {
    return url?.href?.includes('softpos') || url?.hash?.includes('softpos');
  }

  async isAppInstalled(): Promise<boolean> {
    if (!this.settings?.packageName) {
      this.logger.error('Settings not include Adyen App: packageName');
      return false;
    }

    const canOpen: CanOpenURLResult = await AppLauncher.canOpenUrl({ url: this.settings.packageName });
    return canOpen.value;
  }

  async softPosBoardedStatus(): Promise<boolean> {
    if (!this.settings?.targetUrl || !this.settings?.backUrl) {
      this.logger.error("Settings not include urls for App's: softPosBoardedStatus");
      return false;
    }

    const targetAppUrlScheme: string = `${this.settings?.targetUrl}boarded`;
    const returnUrl: string = `${this.settings?.backUrl}://localhost:8080/settings?softpos=${AdyenSoftPosTargetAction.Boarded}`;

    const fullDeepLink: string = `${targetAppUrlScheme}?returnUrl=${returnUrl}`;
    const isAppInstalled: boolean = await this.isAppInstalled();

    if (!isAppInstalled) {
      this.logger.error({ error: 'Payment App not install' }, 'softPosBoardedStatus');
      return isAppInstalled;
    }

    const openedResult: OpenURLResult = await AppLauncher.openUrl({ url: fullDeepLink });
    return openedResult.completed;
  }

  async softPosBoardingProcess(token: string): Promise<boolean> {
    if (!this.settings?.targetUrl || !this.settings?.backUrl) {
      this.logger.error("Settings not include urls for App's: softPosBoardingProcess");
      return false;
    }

    const targetAppUrlScheme: string = `${this.settings?.targetUrl}board`;
    const returnUrl: string = `${this.settings?.backUrl}://localhost:8080/settings?softpos=${AdyenSoftPosTargetAction.Boarding}`;

    const token64Encoded: string = Buffer.from(token).toString('base64');
    const fullDeepLink: string = `${targetAppUrlScheme}?boardingToken=${token64Encoded}&returnUrl=${returnUrl}`;

    const openedResult: OpenURLResult = await AppLauncher.openUrl({ url: fullDeepLink });
    return openedResult.completed;
  }

  async createPaymentToSoftPos(request): Promise<MakePaymentResponse> {
    // Only the identifying fields are logged. The full SaleToPOIRequest adds nothing that the
    // response entry does not already carry, and keeps multi-KB payloads out of the log buffer.
    const { SaleID, ServiceID } = request?.SaleToPOIRequest?.MessageHeader ?? {};
    this.logger.info('createPaymentToSoftPos', { saleID: SaleID, serviceID: ServiceID });
    this.paymentResultSubject$ = new Subject<MakePaymentResponse>();

    const targetAppUrlScheme: string = `${this.settings?.targetUrl}nexo`;
    const returnUrl: string = `${this.settings?.backUrl}://localhost:8080/collection/checkout?softpos=${AdyenSoftPosTargetAction.Payment}`;

    this.adyenUtils.checkNetworkConnection();

    const saleToPOISecuredMessageBase64URL: string = await this.encryptedRequestToString(request);
    const fullDeepLink: string = targetAppUrlScheme + '?request=' + saleToPOISecuredMessageBase64URL + '&returnUrl=' + returnUrl;

    await AppLauncher.openUrl({ url: fullDeepLink });

    return await firstValueFrom(this.paymentResultSubject$
      .pipe(
        filter((v: MakePaymentResponse): boolean => !!v),
        take(1),
        timeout(this.timeForWaitApplicationResponse),
        catchError((error: Error) => {
          if (error.name === 'TimeoutError') {
            return throwError((): AdyenPaymentError => new AdyenPaymentError(
              AdyenPaymentErrors.timeoutWaitingResponse,
              ErrorCondition.SoftPosError,
              ErrorCondition.SoftPosError,
              AdyenPaymentErrors.timeoutWaitingResponse,
              false
            ));
          }
          return throwError((): Error => error);
        })
      ));
  }

  async openPlayStore() {
    const isAvailable = await AppLauncher.canOpenUrl({ url: `market://details?id=${this.settings.packageName}` });

    if (isAvailable.value) {
      await AppLauncher.openUrl({ url: `market://details?id=${this.settings.packageName}` });
    } else {
      await AppLauncher.openUrl({ url: `https://play.google.com/store/apps/details?id=${this.settings.packageName}` });
    }
  }

  public async setResultPaymentFromSoftPos(response: string, hasError: boolean): Promise<void> {

    this.logger.info('setResultPaymentFromSoftPos', { hasError, responseLength: response?.length ?? 0 });

    try {
      const rawPaymentResponse: PaymentResponse = !hasError
        ? await this.decryptedRequestToString(response)
        : { errorCondition: ErrorCondition.SoftPosError, message: response };
      const result: MakePaymentResponse = await this.adyenApiService.softPosResult(rawPaymentResponse, hasError);
      this.logger.info('softPosResult', { result });
      this.emitPaymentResult(result);
    } catch (error) {
      this.logger.error(error, 'setResultPaymentFromSoftPos');
      this.emitPaymentError(error);
    }
  }

  /**
   * The subject only exists while a payment started in this process is waiting. After a cold start
   * (Android reclaimed the app while the payment app was in front) it is undefined, so both emit
   * helpers guard against it instead of throwing a TypeError inside the callback.
   */
  private emitPaymentResult(result: MakePaymentResponse): void {
    if (!this.paymentResultSubject$) {
      this.logger.error('No pending payment to receive the SoftPOS result', 'emitPaymentResult');
      return;
    }
    this.paymentResultSubject$.next(result);
    this.paymentResultSubject$.complete();
    this.paymentResultSubject$ = null;
  }

  private emitPaymentError(error: unknown): void {
    if (!this.paymentResultSubject$) {
      this.logger.error(error, 'No pending payment to receive the SoftPOS error');
      return;
    }
    this.paymentResultSubject$.error(error);
    this.paymentResultSubject$ = null;
  }

  public gettingTargetActionSoftPos(url: URL): string {
    const value: string = (url?.href || '') + '&' + (url?.hash || '');

    if (!value) {
      this.logger.error('Empty return url', 'gettingTargetActionSoftPos');
      return null;
    }

    const match: RegExpMatchArray = value.match(/[\?&]softpos=([^&]+)/);

    if (!match) {
      this.logger.error(`No softpos action in return url ${url?.pathname}`, 'gettingTargetActionSoftPos');
      return null;
    }

    const targetAction: string = match[1];
    this.logger.info('gettingTargetActionSoftPos', { targetAction });
    return targetAction;
  }

  public async saveActiveTerminal(terminal: AdyenTerminalInfo): Promise<void> {
    const fullTerminal: IActiveTerminal = softPOsTerminalHelper(terminal);
    this.localStorage.setObject(StorageKeys.activeTerminal, fullTerminal);
    const paymentService: PaymentService = this.inject.get(PaymentService);
    const paymentMethod: PaymentMethod = await paymentService.createNewPaymentMethod(fullTerminal.title);
    this.posSettingsService.setSelectedPaymentMethod(paymentMethod);
  }

  private async encryptedRequestToString(request): Promise<string> {
    const requestString: string = this.adyenUtils.stringify(request);

    const { SaleToPOIRequest } = request;
    const { MessageHeader } = SaleToPOIRequest;

    const securityKey: SecurityKey | null = await this.getSecuretyKey();

    if (!securityKey) {
      this.logger.error('encryptedRequestToString securityKey not found');
      this.handleError('PaymashPay encryption key not found.');
    }

    const saleToPOIEncryptRequest: SaleToPoiSecuredMessage = await NexoCrypto.encryptAsync(MessageHeader, requestString, securityKey);

    const saleToPOIRequest = {
      SaleToPOIRequest: {
        ...saleToPOIEncryptRequest,
      },
    };
    const message: string = this.adyenUtils.stringify(saleToPOIRequest);
    return AdyenUtils.encodeBase64URL(message);
  }

  /**
   * Fetched once and kept for the lifetime of the process. The key rotates rarely, while this used
   * to cost two HTTPS round-trips per payment (one to encrypt the request, one to decrypt the
   * response) — the decrypt-side call sat on the return path and could stall it for up to the
   * request timeout. Invalidated by {@link invalidateSecurityKey} whenever decryption fails.
   */
  private async getSecuretyKey(): Promise<SecurityKey> {
    if (this.securityKeyCache) {
      return this.securityKeyCache;
    }

    const merchantId = this.localStorage.get(StorageKeys.paymashPayMerchantId);
    if (!merchantId) this.handleError('Please provide PaymashPay MerchantID in the settings');

    const info: AdyenTerminalSettingsInfo = await this.terminalApi.getTerminalSettings(merchantId);

    if (info?.nexo?.encryptionKey && info?.nexo?.encryptionKey.identifier && info?.nexo?.encryptionKey.passphrase) {
      const key = info.nexo.encryptionKey;
      this.securityKeyCache = {
        AdyenCryptoVersion: 1,
        KeyIdentifier: key.identifier,
        KeyVersion: key.version,
        Passphrase: key.passphrase,
      } as SecurityKey;
      return this.securityKeyCache;
    } else {
      this.logger.error({encryptKey: info?.nexo }, 'getSecuretyKey: Keys not found');
      const commonMsg: string = this.translateService.instant('adyen.terminal-provider.adyen-tap-to-pay.app-boarding-token-receive.message-error');
      const message: string = `${commonMsg} (Keys not found.)`;
      throw new AdyenPaymentError(
        message,
        ErrorCondition.SoftPosError,
        ErrorCondition.SoftPosError
        );
    }
  }

  private async decryptedRequestToString(response: string): Promise<unknown> {
    if (!response) {
      this.handleError('PaymashPay response is empty');
    }

    const saleToPOIEncryptRequest: SaleToPoiSecuredMessage = this.adyenUtils.parse<SaleToPoiSecuredMessage>(
      AdyenUtils.decodeBase64URL(response)
    );

    if (!saleToPOIEncryptRequest?.['SaleToPOIResponse']) {
      this.handleError('PaymashPay response could not be parsed');
    }

    const nexo: NexoCrypto = new NexoCrypto();
    const securityKey: SecurityKey | null = await this.getSecuretyKey();

    if (!securityKey) {
      this.logger.error('decryptedRequestToString: SecurityKey not found');
      this.handleError('PaymashPay encryption key not found.');
    }

    try {
      const resp: string = await nexo.decryptAsync(saleToPOIEncryptRequest['SaleToPOIResponse'], securityKey);
      return this.adyenUtils.parse(resp);
    } catch (error) {
      // A cached key that no longer matches looks exactly like a corrupt payload, so drop it and
      // let the next payment refetch instead of failing every decrypt until the app restarts.
      this.invalidateSecurityKey();
      throw error;
    }
  }

  private invalidateSecurityKey(): void {
    this.securityKeyCache = null;
  }

  private handleError(message: string): never {
    throw new AdyenPaymentError(message, ErrorCondition.SoftPosError, ErrorCondition.SoftPosError, message);
  }
}
